import * as React from "react";
import { useParams } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Loader } from "@progress/kendo-react-indicators";

import OEContext from "../../layouts/OEContext";
import UserContext from "../../layouts/UserContext";
import { parseServiceValidationError } from "../../data/helpers/webHelper";
import {
  useMutateSaveClientMenuFieldOverrides,
  useQueryGetAllClientMenuAll,
  useQueryGetClientMenuBasePlans,
  useQueryGetClientMenuFieldOverrides,
} from "../../data/services/clientAdminService";

const SPECIAL_FIELD_LABELS = {
  __PlanName: "Plan Name",
  __RateGroupName: "Rate Group",
  __BenefitGroup: "Benefit Group",
  __EERate: "Employee Rate",
  __ESRate: "Employee + Spouse Rate",
  __ECRate: "Employee + Child Rate",
  __FamRate: "Family Rate",
};

const EDITABLE_FIELD_KEYS = new Set([
  "__EERate",
  "__ESRate",
  "__ECRate",
  "__FamRate",
]);

const VISION_PLAN_OFFER_TYPE = "VIS";
const PLAN_NAME_FIELD_KEY = "__PlanName";

const ClientMenuFieldOverrideEditor = () => {
  const { clientId } = useParams();
  const oePeriod = React.useContext(OEContext);
  const { loggedInUser } = React.useContext(UserContext);
  const isAdmin = Boolean(loggedInUser?.isAdmin);

  const [selectedPlanId, setSelectedPlanId] = React.useState(null);
  const [draftOverrides, setDraftOverrides] = React.useState({});
  const [effectiveOverrides, setEffectiveOverrides] = React.useState({});
  const [ignoreServerOverrides, setIgnoreServerOverrides] = React.useState({});
  const [selectedFieldKeysByPlan, setSelectedFieldKeysByPlan] = React.useState({});
  const [message, setMessage] = React.useState(null);

  const baseMenusQuery = useQueryGetClientMenuBasePlans(
    oePeriod?.oePeriodId,
    clientId
  );
  const shouldUseMenuFallback = baseMenusQuery.error?.response?.status === 404;
  const fallbackMenusQuery = useQueryGetAllClientMenuAll(
    oePeriod?.oePeriodId,
    clientId,
    shouldUseMenuFallback
  );
  const overridesQuery = useQueryGetClientMenuFieldOverrides(
    oePeriod?.oePeriodId,
    clientId
  );
  const saveOverridesMutation = useMutateSaveClientMenuFieldOverrides(
    oePeriod?.oePeriodId,
    clientId
  );

  const {
    isLoading: overridesLoading,
    isError: overridesError,
    data: overridesResponse,
  } = overridesQuery;

  const menusLoading =
    baseMenusQuery.isLoading ||
    (shouldUseMenuFallback && fallbackMenusQuery.isLoading);
  const menusError = shouldUseMenuFallback
    ? fallbackMenusQuery.isError
    : baseMenusQuery.isError;
  const menusResponse = shouldUseMenuFallback
    ? fallbackMenusQuery.data
    : baseMenusQuery.data;

  const overridesByPlan = {};
  (overridesResponse?.data || []).forEach((item) => {
    if (!overridesByPlan[item.planId]) {
      overridesByPlan[item.planId] = {};
    }

    overridesByPlan[item.planId][item.fieldKey] = item.fieldValue;
  });

  const plans = [...(menusResponse?.data || [])].sort((left, right) => {
    const offerTypeCompare = (left.planOfferType || "").localeCompare(
      right.planOfferType || ""
    );

    if (offerTypeCompare !== 0) {
      return offerTypeCompare;
    }

    return `${left.planName || ""}${left.planId || ""}`.localeCompare(
      `${right.planName || ""}${right.planId || ""}`
    );
  });
  const planOptions = plans.map((plan) => ({
    ...plan,
    planLabel: getPlanLabel(
      plan,
      getEffectiveOverrideState(
        plan.planId,
        effectiveOverrides,
        overridesByPlan,
        ignoreServerOverrides
      )
    ),
  }));

  React.useEffect(() => {
    if (!selectedPlanId && plans.length > 0) {
      setSelectedPlanId(plans[0].planId);
    }
  }, [plans, selectedPlanId]);

  React.useEffect(() => {
    if (!message) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setMessage(null);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [message]);

  if (!oePeriod?.oePeriodId) {
    return (
      <CenteredTabState>
        Select an OE period to manage menu overrides.
      </CenteredTabState>
    );
  }

  if (menusLoading) {
    return (
      <CenteredTabState>
        <Loader size="large" type={"infinite-spinner"} />
      </CenteredTabState>
    );
  }

  if (menusError) {
    return (
      <CenteredTabState>
        Unable to load menu plans for this client and OE period.
      </CenteredTabState>
    );
  }

  if (!plans.length) {
    return (
      <CenteredTabState>
        No menu plans found for this client and OE period.
      </CenteredTabState>
    );
  }

  const selectedPlan =
    planOptions.find((plan) => plan.planId === selectedPlanId) || planOptions[0];
  const selectedPlanDraftOverrides = getDraftOverrideState(
    selectedPlan?.planId,
    draftOverrides
  );
  const selectedPlanFieldSelections = getSelectedFieldState(
    selectedPlan?.planId,
    selectedFieldKeysByPlan
  );
  const selectedFieldKeys = getSelectedFieldKeys(selectedPlanFieldSelections);
  const selectedPlanEffectiveOverrides = getEffectiveOverrideState(
    selectedPlan?.planId,
    effectiveOverrides,
    overridesByPlan,
    ignoreServerOverrides
  );
  const fieldRows = buildFieldRows(
    selectedPlan,
    selectedPlanEffectiveOverrides,
    selectedPlanDraftOverrides
  );
  const selectedPlanAllowsAllFields = isVisionPlanOfferType(
    selectedPlan?.planOfferType
  );

  const handlePlanChange = (event) => {
    setSelectedPlanId(event.target.value.planId);
    setMessage(null);
  };

  const handleOverrideChange = (fieldKey, fieldValue) => {
    if (!isEditableFieldKey(fieldKey, selectedPlan?.planOfferType)) {
      return;
    }

    setDraftOverrides((current) => ({
      ...current,
      [selectedPlan.planId]: {
        ...getDraftOverrideState(selectedPlan.planId, current),
        [fieldKey]: fieldValue,
      },
    }));
  };

  const handleFieldSelectionChange = (fieldKey, checked) => {
    if (!isEditableFieldKey(fieldKey, selectedPlan?.planOfferType)) {
      return;
    }

    setSelectedFieldKeysByPlan((current) => ({
      ...current,
      [selectedPlan.planId]: checked
        ? {
            ...getSelectedFieldState(selectedPlan.planId, current),
            [fieldKey]: true,
          }
        : omitFieldKeys(getSelectedFieldState(selectedPlan.planId, current), [fieldKey]),
    }));
  };

  const restorePlanState = (planId, snapshot) => {
    setEffectiveOverrides((current) => ({
      ...current,
      [planId]: snapshot.effectiveOverrides,
    }));
    setIgnoreServerOverrides((current) => ({
      ...current,
      [planId]: snapshot.ignoreServerOverrides,
    }));
    setDraftOverrides((current) => ({
      ...current,
      [planId]: snapshot.draftOverrides,
    }));
    setSelectedFieldKeysByPlan((current) => ({
      ...current,
      [planId]: snapshot.selectedFieldKeys,
    }));
  };

  const synchronizePlanState = async (planId) => {
    const refetches = [overridesQuery.refetch()];

    if (shouldUseMenuFallback) {
      refetches.push(fallbackMenusQuery.refetch());
    }

    const results = await Promise.all(refetches);
    const synchronizationFailed = results.some((result) => result?.error);

    if (!synchronizationFailed) {
      setEffectiveOverrides((current) => omitPlanState(current, planId));
      setIgnoreServerOverrides((current) => omitPlanState(current, planId));
    }
  };

  const handleSave = async () => {
    if (!isAdmin) {
      return;
    }

    setMessage(null);

    const planId = selectedPlan.planId;
    const previousPlanState = {
      effectiveOverrides: selectedPlanEffectiveOverrides,
      draftOverrides: selectedPlanDraftOverrides,
      selectedFieldKeys: selectedPlanFieldSelections,
      ignoreServerOverrides: getIgnoreServerState(planId, ignoreServerOverrides),
    };

    const planOverrides = getOverrideState(planId, draftOverrides);
    const requestedOverrides = buildSaveOverrideList(
      selectedPlanEffectiveOverrides,
      planOverrides,
      selectedPlan?.planOfferType
    );
    const nextEffectiveOverrides = requestedOverrides.reduce((result, item) => {
      result[item.fieldKey] = item.fieldValue;
      return result;
    }, {});

    const payload = {
      clientId,
      oePeriodId: oePeriod?.oePeriodId,
      planId,
      planOfferType: selectedPlan?.planOfferType,
      overrides: requestedOverrides,
    };

    setEffectiveOverrides((current) => ({
      ...current,
      [planId]: nextEffectiveOverrides,
    }));
    setIgnoreServerOverrides((current) => ({
      ...current,
      [planId]: true,
    }));
    setDraftOverrides((current) => ({
      ...current,
      [planId]: {},
    }));
    setSelectedFieldKeysByPlan((current) => ({
      ...current,
      [planId]: {},
    }));

    try {
      await saveOverridesMutation.mutateAsync(payload);
      await synchronizePlanState(planId);
      setMessage({
        tone: "success",
        text: "Overrides Saved",
      });
    } catch (error) {
      restorePlanState(planId, previousPlanState);
      setMessage({
        tone: "error",
        text: getMutationErrorMessage(error),
      });
    }
  };

  const handleReset = async () => {
    if (!isAdmin) {
      return;
    }

    setMessage(null);

    const planId = selectedPlan.planId;

    const previousPlanState = {
      effectiveOverrides: selectedPlanEffectiveOverrides,
      draftOverrides: selectedPlanDraftOverrides,
      selectedFieldKeys: selectedPlanFieldSelections,
      ignoreServerOverrides: getIgnoreServerState(planId, ignoreServerOverrides),
    };

    const payload = {
      clientId,
      oePeriodId: oePeriod?.oePeriodId,
      planId,
      planOfferType: selectedPlan?.planOfferType,
      overrides: [],
    };

    setEffectiveOverrides((current) => ({
      ...current,
      [planId]: {},
    }));
    setIgnoreServerOverrides((current) => ({
      ...current,
      [planId]: true,
    }));
    setDraftOverrides((current) => ({
      ...current,
      [planId]: {},
    }));
    setSelectedFieldKeysByPlan((current) => ({
      ...current,
      [planId]: {},
    }));

    try {
      await saveOverridesMutation.mutateAsync(payload);
      await synchronizePlanState(planId);
      setMessage({
        tone: "success",
        text: "Overrides reset success",
      });
    } catch (error) {
      restorePlanState(planId, previousPlanState);
      setMessage({
        tone: "error",
        text: getMutationErrorMessage(error),
      });
    }
  };

  const handleResetSelected = async () => {
    if (!isAdmin) {
      return;
    }

    if (!selectedFieldKeys.length) {
      return;
    }

    setMessage(null);

    const planId = selectedPlan.planId;

    const previousPlanState = {
      effectiveOverrides: selectedPlanEffectiveOverrides,
      draftOverrides: selectedPlanDraftOverrides,
      selectedFieldKeys: selectedPlanFieldSelections,
      ignoreServerOverrides: getIgnoreServerState(planId, ignoreServerOverrides),
    };
    const nextEffectiveOverrides = omitFieldKeys(
      selectedPlanEffectiveOverrides,
      selectedFieldKeys
    );
    const nextDraftOverrides = omitFieldKeys(
      selectedPlanDraftOverrides,
      selectedFieldKeys
    );

    const payload = {
      clientId,
      oePeriodId: oePeriod?.oePeriodId,
      planId,
      planOfferType: selectedPlan?.planOfferType,
      overrides: buildResetSelectedOverrideList(
        selectedPlanEffectiveOverrides,
        selectedFieldKeys,
        selectedPlan?.planOfferType
      ),
    };

    setEffectiveOverrides((current) => ({
      ...current,
      [planId]: nextEffectiveOverrides,
    }));
    setIgnoreServerOverrides((current) => ({
      ...current,
      [planId]: true,
    }));
    setDraftOverrides((current) => ({
      ...current,
      [planId]: nextDraftOverrides,
    }));
    setSelectedFieldKeysByPlan((current) => ({
      ...current,
      [planId]: {},
    }));

    try {
      await saveOverridesMutation.mutateAsync(payload);
      await synchronizePlanState(planId);
      setMessage({
        tone: "success",
        text: "Selected overrides reset success",
      });
    } catch (error) {
      restorePlanState(planId, previousPlanState);
      setMessage({
        tone: "error",
        text: getMutationErrorMessage(error),
      });
    }
  };

  return (
    <div style={rootContainerStyle}>
      <div style={topSectionStyle}>
        <div style={planSelectorPanelStyle}>
          <h2 style={{ margin: "10px 0" }}>Benefit Menu Field Overrides</h2>
          <p style={{ margin: "0 0 12px 0" }}>
            {selectedPlanAllowsAllFields
              ? "All fields are editable for Vision plans. Leave an override blank to use the system-generated menu value."
              : "Only employee contribution fields are editable for non-Vision plans. Leave an override blank to use the system-generated menu value."}
          </p>
          <div style={planSelectorWrapStyle}>
            <DropDownList
              style={planSelectorStyle}
              data={planOptions}
              dataItemKey="planId"
              textField="planLabel"
              value={selectedPlan}
              onChange={handlePlanChange}
              itemRender={(li, itemProps) =>
                React.cloneElement(li, li.props, getPlanLabel(itemProps.dataItem))
              }
            />
          </div>
        </div>

        {isAdmin ? (
          <div style={actionButtonsStyle}>
            <Button
              themeColor="primary"
              onClick={handleSave}
              disabled={saveOverridesMutation.isLoading}
              style={actionButtonStyle}
            >
              Save Overrides
            </Button>
            <span
              style={buttonTooltipWrapStyle}
              title="Resets only the selected checked fields to their default values."
            >
              <Button
                onClick={handleResetSelected}
                disabled={saveOverridesMutation.isLoading || !selectedFieldKeys.length}
                style={actionButtonStyle}
              >
                Reset Selected Fields
              </Button>
            </span>
            <span
              style={buttonTooltipWrapStyle}
              title="Resets all fields in this list to their default values."
            >
              <Button
                onClick={handleReset}
                disabled={saveOverridesMutation.isLoading}
                style={actionButtonStyle}
              >
                Reset Plan Overrides
              </Button>
            </span>
          </div>
        ) : (
          <div style={actionButtonsStyle}>
            <span style={{ color: "#b45309", fontWeight: 600 }}>
              Read-only access. Only administrators can modify menu overrides.
            </span>
          </div>
        )}
      </div>

      {message ? (
        <div
          style={
            message.tone === "error" ? errorMessageBannerStyle : successMessageBannerStyle
          }
        >
          {message.text}
        </div>
      ) : null}

      {overridesLoading ? (
        <div style={{ marginBottom: 12, color: "#6c757d" }}>
          Loading saved overrides...
        </div>
      ) : null}

      {overridesError ? (
        <div style={{ marginBottom: 12, color: "#842029" }}>
          Saved overrides could not be loaded. You can still enter new override
          values and save them.
        </div>
      ) : null}

      <div style={tableContainerStyle}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: 900,
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "32%" }} />
            <col style={{ width: "28%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={headerCellStyle}>Field</th>
              <th style={headerCellStyle}>Current Menu Value</th>
              <th style={headerCellStyle}>Override Value</th>
              <th style={headerCheckboxCellStyle}>Select</th>
            </tr>
          </thead>
          <tbody>
            {fieldRows.map((row) => (
              <tr key={row.fieldKey}>
                <td style={cellStyle}>
                  <div>{row.label}</div>
                  <div style={{ fontSize: 12, color: "#6c757d" }}>
                    {row.fieldKey}
                  </div>
                </td>
                <td style={cellStyle}>{row.currentValue || <span>&nbsp;</span>}</td>
                <td style={cellStyle}>
                  <textarea
                    rows={2}
                    style={textAreaStyle}
                    value={row.overrideValue}
                    placeholder={row.isEditable ? "Blank means use current system value" : "Read-only field"}
                    disabled={!row.isEditable || !isAdmin}
                    onChange={(event) =>
                      handleOverrideChange(row.fieldKey, event.target.value)
                    }
                  />
                </td>
                <td style={checkboxCellStyle}>
                  <input
                    type="checkbox"
                    checked={Boolean(selectedPlanFieldSelections[row.fieldKey])}
                    disabled={!row.isEditable || !isAdmin}
                    onChange={(event) =>
                      handleFieldSelectionChange(row.fieldKey, event.target.checked)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const isVisionPlanOfferType = (planOfferType) =>
  (planOfferType || "").trim().toUpperCase() === VISION_PLAN_OFFER_TYPE;

export const isEditableFieldKey = (fieldKey, planOfferType) => {
  if (!fieldKey || fieldKey.trim() === "") {
    return false;
  }

  return isVisionPlanOfferType(planOfferType) || EDITABLE_FIELD_KEYS.has(fieldKey);
};

export const buildFieldRows = (plan, effectiveOverrideState, draftOverrideState) => {
  const staticFields = [
    { fieldKey: PLAN_NAME_FIELD_KEY, currentValue: plan.planName || "" },
    { fieldKey: "__RateGroupName", currentValue: plan.rateGroupName || "" },
    { fieldKey: "__BenefitGroup", currentValue: plan.benefitGroup || "" },
    { fieldKey: "__EERate", currentValue: toDisplayValue(plan.eeRate) },
    { fieldKey: "__ESRate", currentValue: toDisplayValue(plan.esRate) },
    { fieldKey: "__ECRate", currentValue: toDisplayValue(plan.ecRate) },
    { fieldKey: "__FamRate", currentValue: toDisplayValue(plan.famRate) },
  ];

  const fieldMap = staticFields.reduce((result, field) => {
    result[field.fieldKey] = field.currentValue;
    return result;
  }, {});

  Object.entries(plan.fields || {})
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .forEach(([fieldKey, currentValue]) => {
      fieldMap[fieldKey] = currentValue || "";
    });

  return Object.entries(fieldMap).map(([fieldKey, currentValue]) => ({
    fieldKey,
    currentValue: effectiveOverrideState[fieldKey] ?? currentValue,
    isEditable: isEditableFieldKey(fieldKey, plan?.planOfferType),
    label: SPECIAL_FIELD_LABELS[fieldKey] || fieldKey,
    overrideValue: draftOverrideState[fieldKey] || "",
  }));
};

const getDraftOverrideState = (planId, draftOverrides) => {
  return draftOverrides[planId] || {};
};

const getEffectiveOverrideState = (
  planId,
  effectiveOverrides,
  overridesByPlan,
  ignoreServerOverrides
) => {
  if (Object.prototype.hasOwnProperty.call(effectiveOverrides, planId)) {
    return effectiveOverrides[planId] || {};
  }

  if (ignoreServerOverrides[planId]) {
    return {};
  }

  return overridesByPlan[planId] || {};
};

const getOverrideState = (planId, draftOverrides) => {
  return draftOverrides[planId] || {};
};

export const buildSaveOverrideList = (
  effectiveOverrideState,
  draftOverrideState,
  planOfferType
) => {
  const nextOverrides = Object.fromEntries(
    Object.entries(effectiveOverrideState || {}).filter(([fieldKey]) =>
      isEditableFieldKey(fieldKey, planOfferType)
    )
  );

  Object.entries(draftOverrideState || {}).forEach(([fieldKey, fieldValue]) => {
    if (!isEditableFieldKey(fieldKey, planOfferType)) {
      return;
    }

    if (fieldValue && fieldValue.trim() !== "") {
      nextOverrides[fieldKey] = fieldValue;
      return;
    }

    delete nextOverrides[fieldKey];
  });

  return Object.entries(nextOverrides)
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([fieldKey, fieldValue]) => ({
      fieldKey,
      fieldValue,
    }));
};

export const buildResetSelectedOverrideList = (
  effectiveOverrideState,
  selectedFieldKeys,
  planOfferType
) => {
  return Object.entries(
    omitFieldKeys(
      Object.fromEntries(
        Object.entries(effectiveOverrideState || {}).filter(([fieldKey]) =>
          isEditableFieldKey(fieldKey, planOfferType)
        )
      ),
      (selectedFieldKeys || []).filter((fieldKey) =>
        isEditableFieldKey(fieldKey, planOfferType)
      )
    )
  )
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([fieldKey, fieldValue]) => ({
      fieldKey,
      fieldValue,
    }));
};

export const omitFieldKeys = (source, fieldKeys) => {
  const nextState = { ...(source || {}) };

  (fieldKeys || []).forEach((fieldKey) => {
    delete nextState[fieldKey];
  });

  return nextState;
};

const omitPlanState = (source, planId) => {
  const nextState = { ...(source || {}) };
  delete nextState[planId];
  return nextState;
};

const getSelectedFieldState = (planId, selectedFieldKeysByPlan) => {
  return selectedFieldKeysByPlan[planId] || {};
};

const getSelectedFieldKeys = (selectedFieldState) => {
  return Object.keys(selectedFieldState || {}).filter(
    (fieldKey) => selectedFieldState[fieldKey]
  );
};

const getIgnoreServerState = (planId, ignoreServerOverrides) => {
  return Boolean(ignoreServerOverrides[planId]);
};

export const getPlanDisplayName = (plan, effectiveOverrideState) => {
  if (!plan) {
    return "";
  }

  return effectiveOverrideState[PLAN_NAME_FIELD_KEY] || plan.planName || "";
};

export const getPlanLabel = (plan, effectiveOverrideState = {}) => {
  if (!plan) {
    return "";
  }

  const offerType = plan.planOfferType ? `${plan.planOfferType} | ` : "";
  return `${offerType}${getPlanDisplayName(plan, effectiveOverrideState)} (${plan.planId})`;
};

const toDisplayValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return `${value}`;
};

const getMutationErrorMessage = (error) => {
  const parsedError = parseServiceValidationError(error);

  if (parsedError.errors.length > 0) {
    return parsedError.errors[0];
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.response?.data?.title) {
    return error.response.data.title;
  }

  if (error?.message) {
    return error.message;
  }

  return "Unable to save overrides right now. Please try again.";
};

const headerCellStyle = {
  borderBottom: "1px solid #d9d9d9",
  textAlign: "left",
  padding: "10px 12px",
  backgroundColor: "#f8f9fa",
};

const cellStyle = {
  borderBottom: "1px solid #e9ecef",
  padding: "10px 12px",
  verticalAlign: "top",
};

const checkboxCellStyle = {
  ...cellStyle,
  textAlign: "center",
  verticalAlign: "middle",
};

const headerCheckboxCellStyle = {
  ...headerCellStyle,
  textAlign: "center",
};

const buttonTooltipWrapStyle = {
  display: "inline-block",
  width: "100%",
};

const actionButtonStyle = {
  width: "100%",
};

const textAreaStyle = {
  width: "100%",
  minHeight: 54,
  boxSizing: "border-box",
  border: "1px solid #9aa4b2",
  borderRadius: 4,
  padding: 8,
  resize: "vertical",
  font: "inherit",
  backgroundColor: "#ffffff",
};

const successMessageBannerStyle = {
  marginBottom: 12,
  padding: "10px 12px",
  borderRadius: 4,
  border: "1px solid #badbcc",
  backgroundColor: "#d1e7dd",
  color: "#0f5132",
};

const errorMessageBannerStyle = {
  marginBottom: 12,
  padding: "10px 12px",
  borderRadius: 4,
  border: "1px solid #f5c2c7",
  backgroundColor: "#f8d7da",
  color: "#842029",
};

const rootContainerStyle = {
  padding: 12,
  width: "100%",
  boxSizing: "border-box",
  display: "block",
};

const topSectionStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "flex-start",
  gap: 16,
  marginBottom: 16,
  width: "100%",
};

const planSelectorPanelStyle = {
  minWidth: 0,
  width: "100%",
};

const planSelectorWrapStyle = {
  display: "inline-block",
  width: "fit-content",
  minWidth: 420,
  maxWidth: "100%",
};

const planSelectorStyle = {
  width: "100%",
  border: "1px solid #9aa4b2",
  borderRadius: 4,
  backgroundColor: "#ffffff",
};

const actionButtonsStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 10,
  minWidth: 190,
  justifySelf: "end",
  alignSelf: "start",
};

const tableContainerStyle = {
  overflowX: "auto",
  border: "1px solid #d9d9d9",
  borderRadius: 6,
  backgroundColor: "#ffffff",
  width: "100%",
};

const CenteredTabState = ({ children }) => {
  return (
    <div
      style={{
        minHeight: 320,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 24,
      }}
    >
      <div>{children}</div>
    </div>
  );
};

export default ClientMenuFieldOverrideEditor;